import { z } from "zod";
import { SystemEvent } from "@erp/shared-types";
import { defineStepConfig } from "../../registry";

/**
 * Emit System Event — publishes a draft-sales-order routing/handoff event so a
 * downstream workflow is triggered. The reusable handoff primitive for OMS v3:
 *
 *   - DRAFT_SALES_ORDER_ROUTED (routing org → target org): set
 *     `targetOrganizationId` (e.g. from classify_organization's
 *     matched_organization_id) so the engine starts the target org's
 *     event-triggered workflow in THAT org (cross-org routing).
 *   - DRAFT_SALES_ORDER_READY (intra-org wf2 → wf3 handoff): omit the target;
 *     the event routes within the emitting org.
 *
 * Backend-only (publishes via SystemEventPublisher); handler in
 * backend/src/workflows/steps/control-flow/.
 */
export const emitSystemEventStep = defineStepConfig({
  type: "emit_system_event",
  name: "Emit System Event",
  description:
    "Publishes a draft sales-order routing/handoff event to trigger a downstream (optionally cross-organization) workflow",
  category: "Control Flow",
  inputSchema: z.object({
    draftSalesOrderId: z
      .string()
      .uuid()
      .describe("The draft sales order the event is about"),
    targetOrganizationId: z
      .string()
      .uuid()
      .optional()
      .describe(
        "For DRAFT_SALES_ORDER_ROUTED: the organization to route the draft to (cross-org). Omit for intra-org handoffs.",
      ),
    inboundEmailId: z
      .string()
      .uuid()
      .optional()
      .describe("Originating inbound email id (optional)"),
  }),
  outputSchema: z.object({
    emitted: z.boolean().describe("Whether the event was published"),
    eventType: z.string().describe("The event type that was emitted"),
  }),
  configSchema: z.object({
    eventType: z
      .union([
        z.literal(SystemEvent.DRAFT_SALES_ORDER_ROUTED),
        z.literal(SystemEvent.DRAFT_SALES_ORDER_READY),
        z.literal(SystemEvent.DRAFT_SALES_ORDER_UNCLASSIFIED),
      ])
      .describe("Which routing/handoff event to emit"),
  }),

  emits: [] as const,

  execution: {
    startToCloseTimeout: "1 minute",
    retry: {
      maximumAttempts: 3,
    },
  },
});
